import { ApiSystem, Asset } from "../state/types";
import { buildSortedImages } from "../components/carousel/carouselHelpers";
import { CameraDirection } from "../components/carousel/cameraTagPosition";

type AnyImage = Record<string, any>;

/** Stable identity for an image across the asset's arrays. */
export function imageKey(image: AnyImage | null | undefined): string {
  if (!image) return "";
  return image.imageUrlCf || image.imageUrl || "";
}

/** The dedicated iframe imageOrdering for the asset's configuration:
 *  `system.configurations[<configName>].clientProcess.iframeImageOrdering`,
 *  or null when unset.
 *
 *  This is the ordering edited in the UMS client under Client Process → Iframe;
 *  it is distinct from the syndication imageOrdering. The configurations map KEY
 *  is `asset.configurationName` (older assets carry it as `asset.config`), not
 *  `clientProcess.name` — those can differ. */
export function getIframeImageOrdering(
  asset: Asset | null | undefined,
  systems: ApiSystem[] | null | undefined,
): Record<string, any> | null {
  if (!asset) return null;

  const dynamicAsset = asset as Asset & Record<string, any>;
  const configName = dynamicAsset.configurationName || dynamicAsset.config;
  if (!configName) return null;

  const system = (systems || []).find((s) => s?.systemCode === asset.systemCode);
  const configuration = system?.configurations?.[configName];
  const ordering = configuration?.clientProcess?.iframeImageOrdering;

  return ordering && typeof ordering === "object" ? ordering : null;
}

/** Images in `images` whose imageTag matches, and that carry a usable URL.
 *  Source order preserved. At most one unless matchAll is set. */
function findImagesByTag(images: any, targetTag: string, matchAll: boolean): AnyImage[] {
  if (!Array.isArray(images)) return [];

  const results: AnyImage[] = [];
  for (const image of images) {
    if (!image || image.imageTag !== targetTag || !imageKey(image)) continue;
    results.push(image);
    if (!matchAll) break;
  }
  return results;
}

/** Resolve the configured display order by walking `ImageOrder` +
 *  `{group}_GroupFallback` against the asset's image arrays.
 *
 *  Mirrors `_resolve_ordered_images` in dynamo-api `asset/get_assets.py` (which
 *  itself mirrors the CRS syndication walk), so the download order matches what
 *  the iframe renders. For each `<group>_<Tag>[!|*]` token: strip the trailing
 *  modifier, split on the first '_', turn hyphens in the tag into spaces, then
 *  walk `<group>_GroupFallback` (a comma list of asset array names). The first
 *  array holding a matching imageTag wins; '*' takes every match in that array,
 *  otherwise just the first. Like the iframe (and unlike CRS), '!' does not
 *  hard-fail — a missing image is simply skipped.
 *
 *  Returns [] when the ordering is unusable, which leaves callers on their
 *  default order. */
export function resolveOrderedImages(
  asset: Asset | null | undefined,
  ordering: Record<string, any> | null | undefined,
): AnyImage[] {
  if (!asset || !ordering) return [];

  const orderStr = ordering.ImageOrder;
  if (typeof orderStr !== "string" || !orderStr.trim()) return [];

  const dynamicAsset = asset as Asset & Record<string, any>;
  const ordered: AnyImage[] = [];

  for (const rawItem of orderStr.split(",")) {
    const item = rawItem.trim();
    if (!item) continue;

    const matchAll = item.endsWith("*");
    const parsable = matchAll || item.endsWith("!") ? item.slice(0, -1) : item;

    const separatorIdx = parsable.indexOf("_");
    if (separatorIdx <= 0 || separatorIdx === parsable.length - 1) continue;

    const group = parsable.slice(0, separatorIdx);
    const targetTag = parsable.slice(separatorIdx + 1).replace(/-/g, " ");

    const fallbackStr = ordering[`${group}_GroupFallback`];
    if (typeof fallbackStr !== "string" || !fallbackStr.trim()) continue;

    for (const assetKey of fallbackStr.split(",").map((k) => k.trim()).filter(Boolean)) {
      const found = findImagesByTag(dynamicAsset[assetKey], targetTag, matchAll);
      if (found.length) {
        ordered.push(...found);
        break;
      }
    }
  }

  return ordered;
}

/** The asset's images in configured iframe order, or [] when its configuration
 *  has no usable iframe ordering. */
export function resolveIframeOrderedImages(
  asset: Asset | null | undefined,
  systems: ApiSystem[] | null | undefined,
): AnyImage[] {
  return resolveOrderedImages(asset, getIframeImageOrdering(asset, systems));
}

/** Map of image key → position in the configured iframe order. Empty when the
 *  configuration has no iframe ordering. */
export function buildIframeOrderRanks(
  asset: Asset | null | undefined,
  systems: ApiSystem[] | null | undefined,
): Map<string, number> {
  const ranks = new Map<string, number>();

  resolveIframeOrderedImages(asset, systems).forEach((image) => {
    const key = imageKey(image);
    if (key && !ranks.has(key)) ranks.set(key, ranks.size);
  });

  return ranks;
}

/** Put one group of images (a single media type) into display order:
 *
 *  1. The configuration's iframe image ordering (Client Process → Iframe) when
 *     it has one — the same order the iframe renders, restricted to these images.
 *  2. Whatever the ordering doesn't name falls back to the carousel's default
 *     sort: camera position for exterior-style images, filename order otherwise.
 *
 *  @param images - Raw image objects for a single media type
 *  @param iframeRanks - Image key → position, from {@link buildIframeOrderRanks}
 *  @param cameraDirection - Rotation direction, for the exterior position sort */
export function orderImagesForDisplay(
  images: any[],
  iframeRanks: Map<string, number>,
  cameraDirection: CameraDirection = "cw",
): AnyImage[] {
  const list = Array.isArray(images) ? images : [];
  if (list.length < 2) return list;

  // Carousel default order. buildSortedImages drops images it can't classify
  // (no URL), so any leftovers are appended in their original order.
  const sorted = buildSortedImages(list, cameraDirection).map((item) => item.idx);
  const seen = new Set(sorted);
  const carouselOrder = [
    ...sorted.map((idx) => list[idx]),
    ...list.filter((_image, idx) => !seen.has(idx)),
  ];

  if (iframeRanks.size === 0) return carouselOrder;

  const ranked: { image: AnyImage; rank: number }[] = [];
  const unranked: AnyImage[] = [];

  for (const image of carouselOrder) {
    const rank = iframeRanks.get(imageKey(image));
    if (rank === undefined) {
      unranked.push(image);
    } else {
      ranked.push({ image, rank });
    }
  }

  ranked.sort((a, b) => a.rank - b.rank);
  return [...ranked.map((entry) => entry.image), ...unranked];
}
